import React, { useCallback, useContext, useMemo, useState } from 'react'
import { FlexView, Card, LoadingOverlay, Button, Modal, Spinner } from '../components/common'
import { useHistory } from "react-router-dom"
import { useTranslation } from 'react-i18next'
import { JobContext } from 'stores/JobStore'
import { UserContext } from 'stores/UserStore'
import { FaArrowRight } from 'react-icons/fa'
import { Checkbox } from 'components/form'
import { IoCloseCircleOutline } from 'react-icons/io5'
import CompareTablePage from 'app-components/home-compare/CompareTable'
import FilterableTable from 'app-components/common/FilterableTable'
import styled from 'styled-components'
import useJobApi from 'api/jobApi'

const BoxlessTable = styled(FilterableTable)`
box-shadow: none;
margin: 0px;
width: 100%; 

table {
  width: 100%;
  height: 100%;
  white-space: nowrap;
  th, td {
    text-align: left;
    border-right: 0;
  }
}
`

const DetailArrow = styled(FaArrowRight)`
  color: #777;
  cursor: pointer;
  margin-left: 40px;
  transition: 0.5s;
   
   &:hover{
    color: #aaa;
   }
`

const Home = () => {
  const history = useHistory()

  const { idToken } = useContext(UserContext)
  const jobApi = useJobApi(idToken)

  const { userJobs, isJobsLoading } = useContext(JobContext)
  const { t } = useTranslation()
  const { registryLog, registryLogOld } = useContext(UserContext)
  // const DEFAULT_URL = "running-expert/home"

  const [isLoadingCompare, setIsLoadingCompare] = useState(false)
  const [openCompareModal, setOpenCompareModal] = useState(false)
  const [compareMode, setCompareMode] = useState(false)
  const [compareItems, setCompareItems] = useState([])
  const [compareItemsDetails, setCompareItemsDetails] = useState([])

  const handleCancelCompare = useCallback(() => {
    if (isLoadingCompare) return null

    setCompareItems([])
    setCompareMode(false)
  }, [isLoadingCompare])

  const handleCloseCompareModal = useCallback(() => {
    setOpenCompareModal(false)
  }, [])

  const handleCompareButton = useCallback(async () => {
    if (compareItems.length <= 0) {
      return handleCancelCompare()
    }

    if (!(compareItems.length >= 2)) {
      return null
    }

    setIsLoadingCompare(true)

    try {

      const compareItemsDetailsRes = await Promise.all(compareItems.map(item => jobApi.getJobDataFromApi(item.job_id)))

      const mappedCompareItemsDetails = compareItemsDetailsRes.map((item, i) => {
        const { highlightData, performanceIndicators, jobInfo } = item
        const pipesApplied = performanceIndicators.find(item => item.id === "pipesApplied") || null
        const duration = performanceIndicators.find(item => item.id === "duration") || null
        const cumulativeLength = performanceIndicators.find(item => item.id === "cumulativeLength") || null
        const avgSpeedJoints = performanceIndicators.find(item => item.id === "avgSpeedJoints") || null
        const avgSpeedMeters = performanceIndicators.find(item => item.id === "avgSpeedMeters") || null
        const cruisingSpeedQuantity = performanceIndicators.find(item => item.id === "cruisingSpeedQuantity") || null
        const cruisingSpeedTime = performanceIndicators.find(item => item.id === "cruisingSpeedTime") || null
        const rejectedQtd = highlightData.find(item => item.id === "rejectedQtd") || null
        const plannedStoppagesQtd = highlightData.find(item => item.id === "plannedStoppagesQtd") || null
        const unplannedStoppagesQtd = highlightData.find(item => item.id === "unplannedStoppagesQtd") || null

        return ({
          jobId: jobInfo?.jobId?.title,
          pipesApplied,
          duration,
          cumulativeLength,
          avgSpeedJoints,
          avgSpeedMeters,
          cruisingSpeedQuantity,
          cruisingSpeedTime,
          rejectedQtd,
          plannedStoppagesQtd,
          unplannedStoppagesQtd,
        })
      })

      setCompareItemsDetails(mappedCompareItemsDetails)
      setOpenCompareModal(true)
    } catch (error) {
      console.log(error.stack)
    } finally {
      setIsLoadingCompare(false)
    }
  }, [jobApi, compareItems, handleCancelCompare])

  const tableColumns = useMemo(() => [
    !compareMode ?
      {
        Header: '',
        id: 'details',
        Cell: ({ cell: { row } }) => <DetailArrow size="1rem" onClick={() => {
          if (row && row.original && row.original.job_id) {
            registryLogOld('click_jobDetails', "running-expert/home")
            registryLog('job_details', "click", "job_details")

            registryLogOld('client_in', "running-expert/job-details")
            registryLog('job_details', "navigation", "client_in")
            history.push(`/job-details/${row.original.job_id}`)
          }
        }} />
      }
      :
      {
        Header: '',
        id: 'compare',
        Cell: ({ cell: { row } }) => compareMode ? <Checkbox display='unset' style={{ margin: 0 }} checked={compareItems.includes(row.original)} onChange={(isChecked) => {
          const rowJob = row.original
          if (isChecked) {
            const limit = 4
            if (compareItems.length < limit) {
              setCompareItems([...compareItems, rowJob])
            }
          } else {
            const itemIdx = compareItems.indexOf(rowJob)
            const filterd = compareItems.filter((_, idx) => idx !== itemIdx)
            setCompareItems(filterd)
          }
        }} /> : null
      },
    {
      Header: t('Drive_job'),
      accessor: 'field_job_number',
      id: 'field_job_number',
    },
    {
      Header: t('Date'),
      accessor: 'job_date',
    },
    {
      Header: t('Well'),
      accessor: 'well',
    },
    {
      Header: t('Well Section'),
      accessor: 'well_section',
    },
    {
      Header: t('Rig'),
      accessor: 'rig_name',
    },
    {
      Header: t('Drilling Contractor'),
      accessor: 'drilling_contractor',
    },
    {
      Header: t('Company'),
      accessor: 'oil_company',
    },
    {
      Header: t('Country'),
      accessor: 'countryFlag',
    },
  ], [compareMode, compareItems, history, registryLog, registryLogOld, t])

  return (
    <FlexView minWidth="100%" minHeight="92.7vh" alignItems="center" justifyContent="flex-start" fontSize="title" fontWeight="bold" backgroundColor="#E5E5E5">
      <FlexView width="95%" alignSelf="center" alignItems="center" justifyContent="center">
        <FlexView alignSelf="stretch">
          <Card alignSelf="stretch" elevation="high" margin="30px 8px">
            {
              !compareMode ? (
                <div style={{ width: "100%", justifySelf: "start" }}>
                  <Button backgroundColor="primary" color="white" style={{ marginLeft: "auto" }} onClick={() => setCompareMode(true)}>
                    {t('CompareJobs')}
                  </Button>
                </div>
              ) : (
                <div style={{ marginLeft: "auto", display: "flex" }}>
                  {
                    isLoadingCompare ? null : <Button backgroundColor="primary" color="white" onClick={() => handleCancelCompare()}>
                      {t('Cancel')}
                    </Button>
                  }
                  <Button backgroundColor="primary" color="white" style={{ marginLeft: "10px" }} onClick={() => handleCompareButton()}>
                    {
                      isLoadingCompare ? (
                        <Spinner size="18px" color='white' />
                      ) : t('Compare')
                    }
                  </Button>
                </div>
              )
            }
            <LoadingOverlay visible={isJobsLoading} fixed={true} />
            <BoxlessTable columns={tableColumns} data={userJobs} sortable="true" paginated="true"></BoxlessTable>
          </Card>
          <Modal isOpen={openCompareModal} onModalExit={() => handleCloseCompareModal()} onOutsideClick={() => handleCloseCompareModal()}>
            <Card width="90%" height="80%" alignItems="center" >
              <IoCloseCircleOutline color='#59CBE4' size={30} style={{ marginLeft: "auto", marginBottom: "20px", cursor: 'pointer' }} onClick={() => handleCloseCompareModal()} />
              <CompareTablePage compareItems={compareItems} compareItemsDetails={compareItemsDetails} />
            </Card>
          </Modal>
        </FlexView>
      </FlexView>
    </FlexView>)

}

export default Home
